import { AuthGoogleIcon } from './auth-google-icon';
import {
  AuthFormError,
  AuthFormHeading,
  AUTH_FORM_SPACING,
  AUTH_PRIMARY_BUTTON,
  AUTH_SECONDARY_BUTTON,
  AuthAlternativeActions,
} from './auth-form-presentation';
import { clearPendingSignupLocale } from '@/lib/auth-locale-preference';
import { ButtonLoader } from '@/components/loaders';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { ArrowLeft } from 'lucide-react';
import { SignupLanguageCard } from './signup-language-card';
import type { useSignUpController } from './use-sign-up-controller';
export function SignUpView(model: ReturnType<typeof useSignUpController>) {
  const {
    onToggleMode,
    authBasePath,
    signupPage,
    copy,
    searchParams,
    router,
    fallbackLanguage,
    isLoading,
    pendingAction,
    error,
    setHasEditedName,
    selectedLanguage,
    setSelectedLanguage,
    setHasExplicitLanguageSelection,
    languageSelectionDeferred,
    setLanguageSelectionDeferred,
    step,
    pendingEmail,
    otpCode,
    setOtpCode,
    sortedLocales,
    form,
    handleEmailChange,
    handleEmailBlur,
    handleLanguageSelect,
    buildSignInParams,
    handleOtpVerification,
    handleResendOtp,
    handleGoogleSignUp,
    handleNextStep,
    handleKeyDown,
    handleFormSubmit,
  } = model;
  return (
    <div className="w-full min-w-0">
      <div className="flex flex-col">
        {step === 2 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const params = new URLSearchParams(searchParams.toString());
              params.delete('step');
              params.delete('email');
              router.push(`${authBasePath}?${params.toString()}`);
            }}
            className="mb-3 w-fit gap-2 self-start"
            disabled={isLoading}
          >
            <ArrowLeft className="size-4 rtl:rotate-180" aria-hidden="true" />
            Back
          </Button>
        )}

        <div>
          <AuthFormHeading
            title={
              <>
                {step === 3
                  ? copy.checkYourEmail
                  : signupPage?.headline || copy.createYourAccount}
              </>
            }
            description={
              <>
                {step === 1
                  ? signupPage?.description || copy.enterYourEmailToContinue
                  : step === 2
                    ? signupPage
                      ? copy.setNamePasswordToContinue
                      : copy.enterFullNameAndPassword
                    : `${copy.verificationCodeSent} ${pendingEmail}`}
              </>
            }
          />
        </div>

        <div>
          <Form {...form}>
            <form
              className={AUTH_FORM_SPACING}
              onSubmit={handleFormSubmit}
              autoComplete="on"
              aria-busy={isLoading}
            >
              {step === 1 && (
                <>
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="gap-1.5">
                        <FormLabel>{copy.emailAddressLabel}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            name="email"
                            placeholder={copy.emailPlaceholder}
                            autoComplete="email"
                            spellCheck={false}
                            disabled={isLoading}
                            onKeyDown={handleKeyDown}
                            onChange={(event) =>
                              handleEmailChange(
                                event.target.value,
                                field.onChange
                              )
                            }
                            onBlur={() =>
                              handleEmailBlur(
                                field.value,
                                field.onBlur,
                                field.onChange
                              )
                            }
                            className="rounded-sm! text-base"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <AuthFormError>{error}</AuthFormError>
                  <Button
                    type="button"
                    className={AUTH_PRIMARY_BUTTON}
                    disabled={isLoading}
                    onClick={handleNextStep}
                  >
                    {copy.continueButton}
                  </Button>
                  <AuthAlternativeActions>
                    <Button
                      type="button"
                      variant="outline"
                      className={AUTH_SECONDARY_BUTTON}
                      disabled={isLoading}
                      onClick={handleGoogleSignUp}
                    >
                      {pendingAction !== 'google' ? <AuthGoogleIcon /> : null}
                      <ButtonLoader
                        isLoading={pendingAction === 'google'}
                        loadingText={copy.continueWithGoogle}
                      >
                        {copy.continueWithGoogle}
                      </ButtonLoader>
                    </Button>
                  </AuthAlternativeActions>
                </>
              )}

              {step === 2 && (
                <>
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="gap-1.5">
                        <FormLabel>{copy.emailAddressLabel}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            name="email"
                            placeholder={copy.emailPlaceholder}
                            autoComplete="email"
                            spellCheck={false}
                            disabled={isLoading}
                            onKeyDown={handleKeyDown}
                            onChange={(event) =>
                              handleEmailChange(
                                event.target.value,
                                field.onChange
                              )
                            }
                            onBlur={() =>
                              handleEmailBlur(
                                field.value,
                                field.onBlur,
                                field.onChange
                              )
                            }
                            className="text-base"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="gap-1.5">
                        <FormLabel>{copy.fullName}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="text"
                            name="name"
                            placeholder={copy.fullName}
                            autoComplete="name"
                            disabled={isLoading}
                            onKeyDown={handleKeyDown}
                            onChange={(event) => {
                              field.onChange(event);
                              setHasEditedName(true);
                            }}
                            className="text-base"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem className="gap-1.5">
                        <FormLabel>{copy.password}</FormLabel>
                        <FormControl>
                          <PasswordInput
                            {...field}
                            name="password"
                            placeholder={copy.createPasswordPlaceholder}
                            autoComplete="new-password"
                            autoFocus
                            disabled={isLoading}
                            onKeyDown={handleKeyDown}
                            className="text-base"
                            toggleClassName="text-muted-foreground hover:text-foreground"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem className="gap-1.5">
                        <FormLabel>{copy.confirmPasswordLabel}</FormLabel>
                        <FormControl>
                          <PasswordInput
                            {...field}
                            name="confirmPassword"
                            placeholder={copy.reEnterPasswordPlaceholder}
                            autoComplete="new-password"
                            disabled={isLoading}
                            onKeyDown={handleKeyDown}
                            className="text-base"
                            toggleClassName="text-muted-foreground hover:text-foreground"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <p className="text-muted-foreground text-start text-xs text-pretty">
                    {copy.passwordMinimumHint}
                  </p>

                  {!languageSelectionDeferred && (
                    <SignupLanguageCard
                      copy={copy}
                      selectedLanguage={selectedLanguage}
                      sortedLocales={sortedLocales}
                      disabled={isLoading}
                      onLanguageSelect={handleLanguageSelect}
                      onDefer={() => {
                        clearPendingSignupLocale(fallbackLanguage);
                        setSelectedLanguage(fallbackLanguage);
                        setHasExplicitLanguageSelection(false);
                        setLanguageSelectionDeferred(true);
                      }}
                    />
                  )}

                  <AuthFormError>{error}</AuthFormError>
                  <Button
                    type="button"
                    className={AUTH_PRIMARY_BUTTON}
                    disabled={isLoading}
                    onClick={handleNextStep}
                  >
                    <ButtonLoader
                      isLoading={pendingAction === 'credentials'}
                      loadingText={copy.continueButton}
                    >
                      {copy.continueButton}
                    </ButtonLoader>
                  </Button>
                </>
              )}

              {step === 3 && (
                <>
                  <div className="space-y-6">
                    <div className="flex flex-col items-center">
                      <Label
                        htmlFor="signup-verification-code"
                        className="mb-3 text-center text-sm font-medium"
                      >
                        {copy.enterVerificationCode}
                      </Label>
                      <InputOTP
                        id="signup-verification-code"
                        maxLength={6}
                        autoFocus
                        containerClassName="justify-center"
                        value={otpCode}
                        onChange={setOtpCode}
                        disabled={isLoading}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>

                    <AuthFormError>{error}</AuthFormError>

                    <Button
                      type="button"
                      className={AUTH_PRIMARY_BUTTON}
                      disabled={isLoading || otpCode.length !== 6}
                      onClick={handleOtpVerification}
                    >
                      <ButtonLoader
                        isLoading={pendingAction === 'verify'}
                        loadingText={copy.verifying}
                      >
                        {copy.verifyAndSignIn}
                      </ButtonLoader>
                    </Button>

                    <div className="flex items-center justify-center gap-4 text-sm">
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        className="text-primary inline-flex min-h-8 items-center font-medium underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
                        disabled={isLoading}
                      >
                        <ButtonLoader
                          isLoading={pendingAction === 'resend'}
                          loadingText={copy.resendCode}
                        >
                          {copy.resendCode}
                        </ButtonLoader>
                      </button>
                      <span className="text-muted-foreground">•</span>
                      <button
                        type="button"
                        onClick={() => {
                          const params = buildSignInParams();
                          params.set('mode', 'signin');
                          router.push(`${authBasePath}?${params.toString()}`);
                        }}
                        className="text-primary inline-flex min-h-8 items-center font-medium underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
                        disabled={isLoading}
                      >
                        {copy.backToSignIn}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </form>
          </Form>

          {step !== 3 && (
            <div className="mt-5 text-center text-sm">
              {copy.alreadyHaveAccount}{' '}
              <button
                type="button"
                onClick={onToggleMode}
                className="text-primary inline-flex min-h-8 cursor-pointer items-center font-medium underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2"
                disabled={isLoading}
              >
                {copy.signIn}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
